/* eslint react-hooks/exhaustive-deps: 0 */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate } from "@/lib/dt";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";

export default function W176ReconciliationPage() {
  const colW = useResizableColumns({ storageKey: "review-w17-6-reconciliation", defaults: [160, 130, 130, 130, 240], mins: [100, 90, 90, 90, 130] });   // Step 11d · add-on
  const { current, currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [state, setState] = useState(null);
  const [conclusion, setConclusion] = useState("");
  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/reconciliation`, { _silent: true });
    setState(r.data); setConclusion(r.data?.reviewer_conclusion || "");
  };
  useEffect(() => { reload(); }, [currentEngId]);
  // Batch 28 · Follow-up 1 · F2 (b): the rows are drafts — one Save / Cancel bar for the table; Save sends only the changed rows,
  // one POST each (the existing field route; a field not changed goes as null = left as it is); a failed row keeps its error.
  const td = useTableDraft({
    rows: state ? asArray(state.rows) : [], getId: (r) => r.fs_head,
    saveRow: (fs_head, patch) => api.post(`/engagements/${currentEngId}/w17/reconciliation/field`,
      { fs_head, fs_amount: "fs_amount" in patch ? Number(patch.fs_amount || 0) : null,
        explanation: "explanation" in patch ? patch.explanation : null }, { _silent: true }),
    reload,
  });
  const conclude = async () => {
    try {
      const r = await api.post(`/engagements/${currentEngId}/w17/reconciliation/conclude`,
                                     { conclusion }, { _silent: true });
      setState(r.data); toast.success(t("w17_6.concluded"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const runRecompute = async () => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/reconciliation/run`, {}, { _silent: true });
      toast.success(t("w17_6.runQueued"));
      setTimeout(reload, 1500);
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-6-loading">{t("common.loading")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-6-audit-only">{t("w17_6.notForAudit")}</div>;
  return (
    <div className="space-y-6" data-testid="w17-6-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-6-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">506(r)</span>
          {t("w17_6.title")}
        </h1>
        <SignOffBar itemType="w17_6" itemKey="W17.6" saveFirst={td.dirty} />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_6.subtitle")}</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base normal-case">{t("w17_6.rowsTitle")}</CardTitle>
          <Button size="sm" variant="outline" onClick={runRecompute} data-testid="w17-6-run">{t("w17_6.runBtn")}</Button>
        </CardHeader>
        <CardContent className="space-y-2 text-[12px]">
          <ResizableTable cols={colW} wrap className="w-full">
            <thead><tr className="border-b bg-slate-50 text-[11px]"><ResizableTh index={0} className="p-1 text-start">{t("w17_6.fsHead")}</ResizableTh><ResizableTh index={1} className="p-1 text-end">{t("w17_6.tbAmount")}</ResizableTh><ResizableTh index={2} className="p-1 text-end">{t("w17_6.fsAmount")}</ResizableTh><ResizableTh index={3} className="p-1 text-end">{t("w17_6.difference")}</ResizableTh><ResizableTh index={4} className="p-1 text-start">{t("w17_6.explanation")}</ResizableTh></tr></thead>
            <tbody>
              {asArray(state.rows).map(r => <ReconRow key={r.fs_head} row={r} draft={td} />)}
            </tbody>
          </ResizableTable>
          <TableSaveBar draft={td} lang={lang} testid="w17-6-table-save" />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_6.reviewerConclusion")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Textarea placeholder={t("w17_6.conclusionPh")} value={conclusion} onChange={e => setConclusion(e.target.value)} className="h-20 text-[12px]" data-testid="w17-6-conclusion" />
          <Button onClick={conclude} disabled={td.dirty} data-testid="w17-6-conclude">{t("w17_6.concludeBtn")}</Button>
          {state.reviewer_signed_at && (
            <div className="text-[11px] text-emerald-800" data-testid="w17-6-signed">
              {t("w17_6.signedBy").replace("{name}", state.reviewer_name || "—").replace("{date}", fmtDate(state.reviewer_signed_at))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReconRow({ row: saved, draft }) {
  const row = draft.rowValue(saved);
  const id = saved.fs_head;
  return (
    <tr className={`border-b ${draft.rowClass(id)}`} data-changed={draft.isChanged(id) ? "1" : "0"} data-failed={draft.errorOf(id) ? "1" : "0"}
        data-testid={`w17-6-row-${row.fs_head}`}>
      <td className="p-1">{row.fs_head}<RowSaveError draft={draft} id={id} testid={`w17-6-row-error-${id}`} /></td>
      <td className="p-1 text-end tabular-nums">{Number(row.tb_amount).toFixed(2)}</td>
      <td className="p-1"><Input value={String(row.fs_amount ?? "")} onChange={e => draft.edit(id, "fs_amount", e.target.value)} className="h-7 text-[11px] text-end" data-testid={`w17-6-row-${row.fs_head}-fs`} /></td>
      <td className={`p-1 text-end tabular-nums ${Math.abs(Number(row.difference)) > 0.01 ? "text-amber-800" : ""}`} data-testid={`w17-6-row-${row.fs_head}-diff`}>{Number(row.difference).toFixed(2)}</td>
      <td className="p-1"><Input value={row.explanation || ""} onChange={e => draft.edit(id, "explanation", e.target.value)} className="h-7 text-[11px]" data-testid={`w17-6-row-${row.fs_head}-expl`} /></td>
    </tr>
  );
}
